import { List } from '@mui/material';
import React, { useMemo, useState, useEffect } from 'react'
import { getVariantText } from '../../../../../../utils/helper';
import { useSelector } from 'react-redux';
import RenderNodeValues from './RenderNodeValues';

const RenderProperty = ({properties, onListUpdate}) => {
    const [propertyList, setPropertyList] = useState(properties);
    const state = useSelector((state) => state.buildingProfileTable);
    const scope = useSelector((state) => state.container.scope);

    // Define specific property node names to show based on subAssetType
    const getFilteredPropertyNodeNames = (subAssetType) => {
      const propertyMappings = {
        'Wohnen': [
          'ANZAHL DER WOHNUNGEN', 'ANZAHL WOHNUNGEN', 'NUMBER OF FLATS', 'WOHNUNGSANZAHL'
        ],
        'Logistik': [
          'TRAGFÄHIGKEIT BODEN', 'FLOOR LOAD CAPACITY', 'BODENBELASTUNG',
          'LICHTE HÖHE', 'CLEAR HEIGHT', 'DECKENHÖHE', 'RAUMHÖHE'
        ],
        'Hotel': [
          'SHORT NAME', 'KURZNAME',
          'LOCATION', 'STANDORT', 'LAGE',
          'CONSTRUCTION YEAR', 'BAUJAHR', 'ERRICHTUNGSJAHR',
          'TYPE OF USE', 'NUTZUNGSART', 'VERWENDUNG',
          'NUMBER OF ROOMS', 'ANZAHL ZIMMER', 'ZIMMERANZAHL',
          'ANZAHL GÄSTEZIMMER', 'NUMBER OF GUESTROOMS', 'GÄSTEZIMMER',
          'NUMBER OF BEDS', 'ANZAHL BETTEN', 'BETTENANZAHL',
          'SUSTAINABILITY CERTIFICATE', 'NACHHALTIGKEITSZERTIFIKAT', 'ZERTIFIKAT'
        ]
      };
      
      return propertyMappings[subAssetType] || [];
    };

    // Check if a property should be shown based on subAssetType
    const shouldShowProperty = (property, subAssetType) => {
      // Define the specific properties that should be filtered based on subAssetType
      const restrictedProperties = [
        'ANZAHL DER WOHNUNGEN', 'ANZAHL WOHNUNGEN', 'NUMBER OF FLATS', 'WOHNUNGSANZAHL',
        'TRAGFÄHIGKEIT BODEN', 'FLOOR LOAD CAPACITY', 'BODENBELASTUNG',
        'LICHTE HÖHE', 'CLEAR HEIGHT', 'DECKENHÖHE', 'RAUMHÖHE',
        'SHORT NAME', 'KURZNAME',
        'LOCATION', 'STANDORT', 'LAGE',
        'CONSTRUCTION YEAR', 'BAUJAHR', 'ERRICHTUNGSJAHR',
        'TYPE OF USE', 'NUTZUNGSART', 'VERWENDUNG',
        'NUMBER OF ROOMS', 'ANZAHL ZIMMER', 'ZIMMERANZAHL',
        'ANZAHL GÄSTEZIMMER', 'NUMBER OF GUESTROOMS', 'GÄSTEZIMMER',
        'NUMBER OF BEDS', 'ANZAHL BETTEN', 'BETTENANZAHL',
        'SUSTAINABILITY CERTIFICATE', 'NACHHALTIGKEITSZERTIFIKAT', 'ZERTIFIKAT'
      ];

      // Check if this property is one of the restricted properties
      const isRestrictedProperty = restrictedProperties.some(restrictedProp =>
        property.nodeName?.toUpperCase().includes(restrictedProp.toUpperCase()) ||
        property.labelTranslation?.some(label => 
          label.title?.toUpperCase().includes(restrictedProp.toUpperCase())
        )
      );

      // If it's not a restricted property, always show it
      if (!isRestrictedProperty) {
        return true;
      }

      // If no subAssetType specified, show all properties (including restricted ones)
      if (!subAssetType) {
        return true;
      }

      // For restricted properties, check if they match the current subAssetType
      const allowedProperties = getFilteredPropertyNodeNames(subAssetType);
      
      const isAllowedForSubAssetType = allowedProperties.some(allowedProp =>
        property.nodeName?.toUpperCase().includes(allowedProp.toUpperCase()) ||
        property.labelTranslation?.some(label => 
          label.title?.toUpperCase().includes(allowedProp.toUpperCase())
        )
      );

      return isAllowedForSubAssetType;
    };

    // Filter properties based on subAssetType from openAsset or from property data
    const openAsset = useSelector((state) => state.openAsset);
    
    useEffect(() => {
      // Get subAssetType from openAsset first, or from the first property's nodeValue if available
      let subAssetType = openAsset?.subAssetType;
      
      // If no subAssetType from openAsset, try to get it from the first property with nodeValue data
      if (!subAssetType && properties.length > 0) {
        const firstPropertyWithData = properties.find(prop => 
          prop.nodeValue && prop.nodeValue.length > 0 && prop.nodeValue[0].subAsset
        );
        if (firstPropertyWithData) {
          subAssetType = firstPropertyWithData.nodeValue[0].subAsset.subAssetType;
        }
      }
      
      // Filter properties based on subAssetType
      const filteredProperties = properties.filter(property => 
        shouldShowProperty(property, subAssetType)
      );
      setPropertyList(filteredProperties);
    }, [properties, openAsset?.subAssetType]);

    // Custom ordering for specific properties
    const getCustomOrder = (property, subAssetType) => {
      if (!subAssetType) return property.order || 999;

      // For Hotel: Define the exact order sequence
      if (subAssetType === 'Hotel') {
        const hotelOrder = [
          'SHORT NAME', 'KURZNAME',
          'LOCATION', 'STANDORT', 'LAGE',
          'CONSTRUCTION YEAR', 'BAUJAHR', 'ERRICHTUNGSJAHR',
          'TYPE OF USE', 'NUTZUNGSART', 'VERWENDUNG',
          'NUMBER OF ROOMS', 'ANZAHL ZIMMER', 'ZIMMERANZAHL',
          'NUMBER OF GUESTROOMS', 'ANZAHL GÄSTEZIMMER', 'GÄSTEZIMMER',
          'NUMBER OF BEDS', 'ANZAHL BETTEN', 'BETTENANZAHL',
          'SUSTAINABILITY CERTIFICATE', 'NACHHALTIGKEITSZERTIFIKAT', 'ZERTIFIKAT'
        ];

        // Find matching property in the ordered list
        for (let i = 0; i < hotelOrder.length; i++) {
          const orderKey = hotelOrder[i];
          if (property.nodeName?.toUpperCase().includes(orderKey.toUpperCase()) ||
              property.labelTranslation?.some(label => 
                label.title?.toUpperCase().includes(orderKey.toUpperCase())
              )) {
            return i + 1; // Return position in the sequence (1-based)
          }
        }
      }

      // For Wohnen: Only adjust number of flats to come after underground floors
      if (subAssetType === 'Wohnen') {
        const isUndergroundFloor = ['UNTERGESCHOSS', 'BASEMENT', 'KELLER', 'UNDERGROUND'].some(key =>
          property.nodeName?.toUpperCase().includes(key.toUpperCase()) ||
          property.labelTranslation?.some(label => 
            label.title?.toUpperCase().includes(key.toUpperCase())
          )
        );

        const isNumberOfFlats = ['ANZAHL DER WOHNUNGEN', 'ANZAHL WOHNUNGEN', 'NUMBER OF FLATS', 'WOHNUNGSANZAHL'].some(key =>
          property.nodeName?.toUpperCase().includes(key.toUpperCase()) ||
          property.labelTranslation?.some(label => 
            label.title?.toUpperCase().includes(key.toUpperCase())
          )
        );

        if (isUndergroundFloor) {
          return property.order || 999; // Keep original order for underground floors
        }

        if (isNumberOfFlats) {
          return 'AFTER_UNDERGROUND';
        }
      }

      // Return original order for all other properties
      return property.order || 999;
    };

    const updatePropertyList = (item, nodeValues) => {
      let temp = [...propertyList];
      const index = temp.findIndex((t) => t.nodeIdent === item.nodeIdent);
      if(index !== -1) {
        temp[index] = {...temp[index], nodeValue: nodeValues, isUpdated: true};
      }
      setPropertyList(temp);
      onListUpdate(temp);
    }

    const sortedItemList = useMemo(() => {
      // Get subAssetType for custom ordering
      let subAssetType = openAsset?.subAssetType;
      
      // If no subAssetType from openAsset, try to get it from the first property with nodeValue data
      if (!subAssetType && properties.length > 0) {
        const firstPropertyWithData = properties.find(prop => 
          prop.nodeValue && prop.nodeValue.length > 0 && prop.nodeValue[0].subAsset
        );
        if (firstPropertyWithData) {
          subAssetType = firstPropertyWithData.nodeValue[0].subAsset.subAssetType;
        }
      }

      // Apply custom sorting based on subAssetType
      if (subAssetType === 'Hotel') {
        // For Hotel: Use the strict custom ordering
        return propertyList.toSorted((a, b) => {
          const orderA = getCustomOrder(a, subAssetType);
          const orderB = getCustomOrder(b, subAssetType);
          
          if (orderA !== orderB) {
            return orderA - orderB;
          }
          
          // Fall back to original order if custom order is the same
          return (a.nodeOrder || a.order || 999) - (b.nodeOrder || b.order || 999);
        });
      } else if (subAssetType === 'Wohnen') {
        // For Wohnen: Special handling for number of flats after underground floors
        return propertyList.toSorted((a, b) => {
          const orderA = getCustomOrder(a, subAssetType);
          const orderB = getCustomOrder(b, subAssetType);
          
          // Handle the special case where number of flats should come after underground floors
          if (orderA === 'AFTER_UNDERGROUND') {
            // Find the maximum order of underground floor properties
            const undergroundProperties = propertyList.filter(prop => {
              const propOrder = getCustomOrder(prop, subAssetType);
              return propOrder !== 'AFTER_UNDERGROUND' && ['UNTERGESCHOSS', 'BASEMENT', 'KELLER', 'UNDERGROUND'].some(key =>
                prop.nodeName?.toUpperCase().includes(key.toUpperCase()) ||
                prop.labelTranslation?.some(label => 
                  label.title?.toUpperCase().includes(key.toUpperCase())
                )
              );
            });
            
            const maxUndergroundOrder = undergroundProperties.length > 0 
              ? Math.max(...undergroundProperties.map(prop => prop.nodeOrder || prop.order || 0))
              : 0;
            
            if (orderB === 'AFTER_UNDERGROUND') {
              return 0; // Both are number of flats, maintain relative order
            }
            
            if (orderB !== 'AFTER_UNDERGROUND' && ((b.nodeOrder || b.order || 0) <= maxUndergroundOrder)) {
              return 1; // a (number of flats) comes after b (underground floors)
            }
            
            return (maxUndergroundOrder + 0.5) - (b.nodeOrder || b.order || 999);
          }
          
          if (orderB === 'AFTER_UNDERGROUND') {
            // Find the maximum order of underground floor properties
            const undergroundProperties = propertyList.filter(prop => {
              const propOrder = getCustomOrder(prop, subAssetType);
              return propOrder !== 'AFTER_UNDERGROUND' && ['UNTERGESCHOSS', 'BASEMENT', 'KELLER', 'UNDERGROUND'].some(key =>
                prop.nodeName?.toUpperCase().includes(key.toUpperCase()) ||
                prop.labelTranslation?.some(label => 
                  label.title?.toUpperCase().includes(key.toUpperCase())
                )
              );
            });
            
            const maxUndergroundOrder = undergroundProperties.length > 0 
              ? Math.max(...undergroundProperties.map(prop => prop.nodeOrder || prop.order || 0))
              : 0;
            
            if ((a.nodeOrder || a.order || 0) <= maxUndergroundOrder) {
              return -1; // a (underground floors) comes before b (number of flats)
            }
            
            return (a.nodeOrder || a.order || 999) - (maxUndergroundOrder + 0.5);
          }
          
          // Normal comparison for other properties
          return (a.nodeOrder || a.order || 999) - (b.nodeOrder || b.order || 999);
        });
      } else {
        // For other subAssetTypes: Use original order
        return propertyList.toSorted((a, b) => (a.nodeOrder || a.order || 999) - (b.nodeOrder || b.order || 999));
      }
    }, [propertyList, openAsset?.subAssetType, properties]);

    const getPropertyName = (property) => {
        const inputObj = property?.labelTranslation.find(
          (e) => e.language === scope.projectLanguageCode
        );
        return getVariantText(inputObj.title || property?.name, "body1", {
          fontWeight: "bold",
          color: "#254a9a",
          margin: 5,
          textTransform: "uppercase",
        });
      };

  return (
    <>
       {sortedItemList.map((item, index) => {
              return (
                <tr
                  key={`section-${index}`}
                  id={item.nodeIdent}
                >
                  <td
                    id={`text-${item.nodeIdent}`}
                    style={{
                      wordWrap: "break-word",
                      width: "25%",
                      fontSize: "12px",
                      verticalAlign: "baseline",
                    }}
                  >
                    <div className="sticky-text p-1"
                    >
                    {getPropertyName(item)}
                    </div>
                  </td>
                  <td style={{ width: "75%" }}>
                    <List className={"w-100 p-0"}>
                      <RenderNodeValues item={item} 
                      onUpdate={(item, nodeValue) =>{
                        updatePropertyList(item, nodeValue)
                      }}/>
                    </List>
                  </td>
                </tr>
              );
            })}
            </>
  )
}

export default RenderProperty